import { skipToken } from "@reduxjs/toolkit/query";
import type { DateRange } from "@repo/app-types/entities/dateRange";
import { buildOutcomeGreenResultLabel } from "@repo/app-types/entities/outcome";
import {
  checkPlaybookHasKnowledgeBaseAdherence,
  getOutcomeDefinitionFromPlaybook,
} from "@repo/app-types/entities/playbook";
import type { AdminApiTeammateAgent } from "@repo/app-types/entities/teammateAgent";
import type { ResolvedTeamPerformancePageRow } from "@repo/app-types/entities/teammatePerformanceSummary";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { CenteredModal } from "@repo/ui/Modal/centeredModal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import { useNow } from "@repo/ui/timestamps/timestampContext";
import { IconPlus, IconTool } from "@tabler/icons-react";
import { type FC, useMemo } from "react";
import { Dialog, DialogTrigger } from "react-aria-components";
import { useNavigate } from "react-router";
import { usePlaybookTeammatePerformanceQuery } from "../api/entities/performancePlaybook.js";
import { useAllMinimalPlaybooksQuery } from "../api/entities/playbook.js";
import { useAllTeammateAgentsQuery } from "../api/entities/teammateAgent.js";
import { appPaths } from "../app/appPaths.js";
import { PageBody, PageLayout } from "../components/layout/pageLayout.js";
import { CreateAgentForm } from "../features/agents/createAgentForm.js";
import { buildPlaybookScope } from "../features/filters/buildFilterQueryArgs.js";
import { useFilters } from "../features/filters/filters.js";
import {
  computePassRatePillColor,
  GradingPassRatePill,
} from "../features/teammatePerformance/gradingPassRatePill.js";
import { OutcomeRatePill } from "../features/teammatePerformance/outcomeRatePill.js";
import { PercentagePill } from "../features/teammatePerformance/percentagePill.js";
import { TrendChart } from "../features/teammatePerformance/trendChart.js";
import { Can } from "../helpers/teammateAuthorizationContext.js";

// Helper component to fetch and render performance data for an agent
const AgentPerformanceCell: FC<{
  agent: AdminApiTeammateAgent;
  dateRange: DateRange;
  render: (
    performance: ResolvedTeamPerformancePageRow | undefined,
    isLoading: boolean,
  ) => React.ReactNode;
}> = ({ agent, dateRange, render }) => {
  const now = useNow();

  const { data: performance, isFetching } = usePlaybookTeammatePerformanceQuery(
    agent.teammate_agent_config.playbook_id
      ? {
          playbookId: agent.teammate_agent_config.playbook_id,
          teammateId: agent.id,
          scope: buildPlaybookScope(
            now,
            agent.teammate_agent_config.playbook_id,
            dateRange,
          ),
          filters: [],
        }
      : skipToken,
  );

  return render(performance, isFetching);
};

export const TeammateAgentsPage: FC = () => {
  const navigate = useNavigate();

  const { data: minimalPlaybooks = [], isLoading: minimalPlaybooksLoading } =
    useAllMinimalPlaybooksQuery();

  const { data: agents, isLoading, isError } = useAllTeammateAgentsQuery();

  const [{ dateRange }, renderFilters] = useFilters(["dateRange"]);

  const columns = useMemo(
    (): readonly EntityTableColumnDefinition<AdminApiTeammateAgent>[] => [
      {
        id: "name",
        isRowHeader: true,
        header: "Name",
        renderCell: (agent) => {
          return (
            <div className="flex items-center">
              <span className="w-0 flex-auto truncate font-medium">
                {agent.name}
              </span>
            </div>
          );
        },
      },
      {
        id: "playbookScore",
        className: "w-0",
        header: "Playbook",
        renderCell: (agent) => {
          return (
            <AgentPerformanceCell
              agent={agent}
              dateRange={dateRange}
              render={(performance, isLoading) => {
                return (
                  <div className="flex items-center gap-1">
                    <div className="w-[50px]">
                      <GradingPassRatePill
                        size={Size.SMALL}
                        passRate={performance?.scores.pass_rate}
                        isLoading={isLoading}
                      />
                    </div>
                  </div>
                );
              }}
            />
          );
        },
      },
      {
        id: "outcomeScore",
        header: "Outcome",
        renderCell: (agent) => {
          const playbook = minimalPlaybooks.find(
            (p) => p.id === agent.teammate_agent_config.playbook_id,
          );
          const outcomeDefinition = getOutcomeDefinitionFromPlaybook(playbook);

          return (
            <AgentPerformanceCell
              agent={agent}
              dateRange={dateRange}
              render={(performance, isLoading) => {
                const outcomeRate = performance?.scores.outcome_green_rate;

                // Only show the column content if there's outcome data
                if (outcomeRate === undefined && !isLoading) {
                  return <span className="text-muted-foreground">--</span>;
                }

                return (
                  <div className="flex items-center gap-1">
                    <div className="w-[50px]">
                      <OutcomeRatePill
                        size={Size.SMALL}
                        outcomeRate={outcomeRate}
                        isLoading={isLoading}
                        label={
                          outcomeDefinition
                            ? buildOutcomeGreenResultLabel(outcomeDefinition)
                            : undefined
                        }
                      />
                    </div>
                  </div>
                );
              }}
            />
          );
        },
      },
      {
        id: "knowledgeBaseAdherenceScore",
        className: "w-0",
        header: "KB",
        renderCell: (agent) => {
          const playbook = minimalPlaybooks.find(
            (p) => p.id === agent.teammate_agent_config.playbook_id,
          );

          if (
            playbook === undefined ||
            !checkPlaybookHasKnowledgeBaseAdherence(playbook)
          ) {
            return <span className="text-muted-foreground">--</span>;
          }

          return (
            <AgentPerformanceCell
              agent={agent}
              dateRange={dateRange}
              render={(performance, isLoading) => {
                const incorrectRate = performance?.scores.incorrect_call_ratio;
                const correctRate =
                  incorrectRate !== undefined ? 1 - incorrectRate : undefined;

                return (
                  <PercentagePill
                    size={Size.SMALL}
                    value={correctRate}
                    isLoading={isLoading}
                    computeColor={computePassRatePillColor}
                  />
                );
              }}
            />
          );
        },
      },
      {
        id: "activity",
        header: `Activity (${Math.round(dateRange.length.as("days"))}d)`,
        renderCell: (agent) => {
          return (
            <AgentPerformanceCell
              agent={agent}
              dateRange={dateRange}
              render={(performance, isLoading) => {
                if (isLoading) {
                  return (
                    <div className="h-8 w-full animate-pulse rounded-sm bg-gray-200" />
                  );
                }

                const callVolumeData =
                  performance?.recording_count_sparkline ?? [];

                return (
                  <TrendChart
                    className="h-8"
                    lines={[{ key: "callVolume", data: callVolumeData }]}
                    minYRange={5}
                    renderValue={(value) => value.toString()}
                    showsXAxisLabels={false}
                    showsYAxisLabels={true}
                    getColorClassName={() => "text-muted-foreground"}
                  />
                );
              }}
            />
          );
        },
      },
    ],
    [minimalPlaybooks, dateRange],
  );

  return (
    <PageLayout>
      <PageHeader
        width={PageWidth.Standard}
        title="Agents"
        actions={
          <Can
            I={PermissionActions.UPDATE}
            a={PermissionSubjects.TEAMMATES}
            passThrough={true}
          >
            {(allowed) => (
              <>
                <Button
                  size={Size.SMALL}
                  variant={ButtonVariant.Secondary}
                  // TODO: Make this a link that looks like a button so I can Cmd+click to open in a new tab/window
                  onClick={() => navigate(appPaths.agentTools.build())}
                  isDisabled={!allowed}
                  icon={<IconTool className="h-4 w-4" />}
                >
                  Manage tools
                </Button>

                <DialogTrigger>
                  <Button
                    size={Size.SMALL}
                    variant={ButtonVariant.Primary}
                    icon={<IconPlus className="h-4 w-4" />}
                    isDisabled={!allowed}
                  >
                    Create agent
                  </Button>

                  <CenteredModal isDismissable size={Size.LARGE}>
                    <Dialog className="px-4 pt-5 pb-4 text-left sm:p-6">
                      {({ close }) => (
                        <>
                          <ModalHeader onClose={close}>
                            Create a new agent
                          </ModalHeader>
                          <CreateAgentForm close={close} />
                        </>
                      )}
                    </Dialog>
                  </CenteredModal>
                </DialogTrigger>
              </>
            )}
          </Can>
        }
        filterBar={renderFilters({ isLoading })}
      />

      <PageBody width={PageWidth.Standard}>
        <EntityTable
          aria-label="Teammate agents"
          columns={columns}
          entities={agents ?? []}
          isLoading={isLoading}
          isError={isError}
          emptyMessage="No teammate agents created yet."
          onEntityAction={(agent) =>
            navigate(
              appPaths.teammateAgent.build({ teammateAgentId: agent.id }),
            )
          }
        />
      </PageBody>
    </PageLayout>
  );
};
